/**
 * Deterministic per-instance ids for rendered blocks — the value behind
 * {@code data-<ns>-id}. Pure (no React); the tree walker in
 * {@code ComposedTreeRenderer} feeds it the page's blocks in document
 * order and writes the results back as the reserved {@code _gbtId} prop.
 *
 * <h3>Rules (first match wins)</h3>
 * <ol>
 *   <li>{@code props._anchor} — the operator-authored slug. Rock stable;
 *       what onboarding + pinned tests should target.</li>
 *   <li>{@code props._nodeId} — a stable id from the render payload, when
 *       one is present.</li>
 *   <li>A "keyish" prop ({@link KEYISH_PROPS}) — {@code source} / {@code slug}
 *       / … → {@code "CourseRow-popular"}. Readable, survives reordering.</li>
 *   <li>Positional — {@code "CourseRow-2"} (the 2nd CourseRow in document
 *       order). Always suffixed. Shifts if the operator reorders / deletes
 *       blocks, so not for onboarding.</li>
 * </ol>
 *
 * <p>Collisions (two blocks that resolve to the same base) get a
 * {@code -1} / {@code -2} … suffix in document order.
 */

/** Props consulted, in order, for a readable content key. */
export const KEYISH_PROPS = [
  '_anchor',
  '_nodeId',
  'source',
  'slug',
  'feed',
  'kind',
  'preset',
  'variant',
  'name',
] as const;

const COMBINING_MARKS = /[̀-ͯ]/g;
const NON_ALNUM = /[^a-z0-9]+/g;
const EDGE_DASHES = /^-+|-+$/g;

/** lowercase, non-alphanumeric → single dash, trimmed, capped at 64. */
export function slugify(value: string): string {
  return value
    .normalize('NFKD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    .replace(NON_ALNUM, '-')
    .replace(EDGE_DASHES, '')
    .slice(0, 64);
}

interface BlockLike {
  type: string;
  props?: Record<string, unknown> | null;
}

/**
 * The base id for one block, before collision suffixing.
 *
 * <ul>
 *   <li>operator anchor / payload node id → used verbatim (already an id);</li>
 *   <li>keyish prop → {@code `${type}-${slug(value)}`};</li>
 *   <li>nothing → {@code type} (the caller always adds a positional suffix).</li>
 * </ul>
 *
 * The {@code positional} flag marks a bare-type base, which always takes a
 * positional suffix even when unique.
 */
export function blockBaseId(block: BlockLike): { base: string; positional: boolean } {
  const props = block.props ?? {};
  for (const key of KEYISH_PROPS) {
    const raw = props[key];
    const str = typeof raw === 'string' ? raw.trim() : typeof raw === 'number' ? String(raw) : '';
    if (!str) continue;
    if (key === '_anchor' || key === '_nodeId') return { base: slugify(str), positional: false };
    return { base: `${block.type}-${slugify(str)}`, positional: false };
  }
  return { base: block.type, positional: true };
}

/**
 * Assign a unique id to each block. Input is every block on the page in
 * document order (top-level slots then nested container slots, depth
 * first). Output is the parallel id array.
 */
export function assignBlockIds(blocks: readonly BlockLike[]): string[] {
  const bases = blocks.map(blockBaseId);

  const total = new Map<string, number>();
  for (const { base } of bases) total.set(base, (total.get(base) ?? 0) + 1);

  const seen = new Map<string, number>();
  return bases.map(({ base, positional }) => {
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    if (!positional && (total.get(base) ?? 0) === 1) return base;
    return `${base}-${n}`;
  });
}
